import type {
  DTOPgPrice,
  DTOSubscriptionPageInfo,
  DTOSubscriptionPageModule,
} from 'src/apis/dto/DTOSubscriptionPage';
import type { PriceInfo } from 'src/components/AzarOnly/Subscription/types';

export interface PurchasablePlan {
  page: DTOSubscriptionPageInfo;
  planModule: DTOSubscriptionPageModule & { pgPrice: DTOPgPrice; productId: string };
}

const isPurchasable = (
  m: DTOSubscriptionPageModule | undefined
): m is DTOSubscriptionPageModule & { pgPrice: DTOPgPrice; productId: string } =>
  !!m?.pgPrice && !!m.productId;

export const findPurchasablePlan = (
  pages: DTOSubscriptionPageInfo[],
  pageId: string
): PurchasablePlan | undefined => {
  const page = pages.find((p) => p.pageId === pageId);
  const planModule = page?.sections.find((s) => s.sectionType === 'SUB_PLANS')?.modules?.[0];
  if (!page || !isPurchasable(planModule)) return undefined;

  return {
    page,
    planModule,
  };
};

export const getPlanTermsTexts = (page: DTOSubscriptionPageInfo): string[] | undefined =>
  page.sections
    .find((s) => s.sectionType === 'SUB_TERMS')
    ?.modules?.filter((m) => m.moduleType === 'SUB_TERM')
    .map((m) => m.text ?? '')
    .filter(Boolean);

const PERIOD_UNIT_MAP: Record<string, string> = {
  MONTHS: 'month',
  YEARS: 'year',
  DAYS: 'day',
  HOURS: 'hour',
  MINUTES: 'minute',
};

export const formatPeriodLabel = (period: number, periodUnit: string, locale = 'en'): string => {
  if (periodUnit === 'DAYS' && period % 7 === 0) {
    return new Intl.NumberFormat(locale, {
      style: 'unit',
      unit: 'week',
      unitDisplay: 'long',
    }).format(period / 7);
  }

  const unit = PERIOD_UNIT_MAP[periodUnit];
  if (!unit) return `${period}`;

  return new Intl.NumberFormat(locale, { style: 'unit', unit, unitDisplay: 'long' }).format(period);
};

export const formatPrice = (pgPrice: DTOPgPrice, language = 'en') =>
  new Intl.NumberFormat(language, {
    style: 'currency',
    currency: pgPrice.currency,
  }).format(pgPrice.price);

type PriceFormatInput = {
  pgPrice: DTOPgPrice;
  introductoryPgPrice?: DTOPgPrice;
  period?: number;
  periodUnit?: string;
};

export const toPriceInfo = (input: PriceFormatInput, language: string): PriceInfo => {
  const formatWithPeriod = (price: string, period?: number, periodUnit?: string) => {
    if (!period || !periodUnit) return price;
    return `${price} / ${formatPeriodLabel(period, periodUnit, language)}`;
  };

  const formattedPrice = formatPrice(input.pgPrice, language);
  const formattedIntroductoryPrice = input.introductoryPgPrice
    ? formatPrice(input.introductoryPgPrice, language)
    : undefined;

  return {
    formattedPrice,
    formattedPriceWithPeriod: formatWithPeriod(formattedPrice, input.period, input.periodUnit),
    formattedIntroductoryPrice,
    formattedIntroductoryPriceWithPeriod: formattedIntroductoryPrice
      ? formatWithPeriod(formattedIntroductoryPrice, input.period, input.periodUnit)
      : undefined,
  };
};
